import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Typography } from '@mui/material';
import { useDopamenu } from '../contexts/DopamenuContext';
import { MENU_CATEGORIES } from '../utils/dopamenu';
import { MAX_REWARDS } from '../utils/usePomodoro';
import { ToolButton, toolTextSx } from './ToolCard';

export default function DopamenuImportDialog({ rewards, onImport, onClose }) {
  const { state } = useDopamenu();
  const [selectedIds, setSelectedIds] = useState([]);
  const existing = new Set(rewards.map((reward) => reward.toLowerCase()));
  const available = state.items.filter((item) => !existing.has(item.title.toLowerCase()));
  const capacity = MAX_REWARDS - rewards.length;
  const selected = available.filter((item) => selectedIds.includes(item.id)).slice(0, capacity);
  const selectedSet = new Set(selected.map((item) => item.id));
  const toggle = (id) => setSelectedIds((ids) => selectedSet.has(id) ? ids.filter((value) => value !== id) : [...selected.map((item) => item.id), id]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="import-dopamenu-title" PaperProps={{ sx: { border: '2px solid', borderColor: 'primary.main', borderRadius: 5 } }}>
      <DialogTitle id="import-dopamenu-title" sx={{ fontSize: '0.85rem', lineHeight: 1.9, textAlign: 'center' }}>Import from your dopamenu</DialogTitle>
      <DialogContent>
        {state.items.length ? <>
          <Typography sx={{ ...toolTextSx, textAlign: 'center', color: 'text.primary', mb: 2 }}>Choose activities to add to your timer rewards.</Typography>
          {capacity === 0 && <Typography sx={{ ...toolTextSx, color: 'secondary.main', mb: 2 }}>Your timer has 50 rewards. Remove one to make room.</Typography>}
          <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
            <ToolButton onClick={() => setSelectedIds(available.slice(0, capacity).map((item) => item.id))} disabled={!available.length || !capacity} sx={{ fontSize: '0.6rem' }}>Select available</ToolButton>
            <ToolButton onClick={() => setSelectedIds([])} disabled={!selected.length} sx={{ fontSize: '0.6rem' }}>Clear selection</ToolButton>
          </Stack>
          {MENU_CATEGORIES.map((group) => {
            const items = state.items.filter((item) => item.category === group.id);
            if (!items.length) return null;
            return <Box component="section" aria-label={group.title} key={group.id} sx={{ mb: 2 }}>
              <Typography component="h3" sx={{ ...toolTextSx, textAlign: 'center', m: 0, mb: 1, color: 'secondary.main' }}>{group.title}</Typography>
              {items.map((item) => {
                const alreadyAdded = existing.has(item.title.toLowerCase());
                return <FormControlLabel key={item.id} sx={{ display: 'flex', alignItems: 'flex-start', m: 0, my: 0.5, '& .MuiFormControlLabel-label': { minWidth: 0 } }}
                  control={<Checkbox checked={alreadyAdded || selectedSet.has(item.id)} onChange={() => toggle(item.id)}
                    disabled={alreadyAdded || (!selectedSet.has(item.id) && selected.length >= capacity)} inputProps={{ 'aria-label': item.title }} />}
                  label={<Box sx={{ py: 1 }}><Typography sx={{ ...toolTextSx, color: 'text.primary', overflowWrap: 'anywhere' }}>{item.title}</Typography>
                    {alreadyAdded && <Typography sx={{ ...toolTextSx, fontSize: '0.55rem' }}>Already added</Typography>}</Box>} />;
              })}
            </Box>;
          })}
        </> : <Typography sx={{ ...toolTextSx, color: 'text.primary' }}>Your dopamenu is empty. Add a few activities, then choose which ones to use as rewards here.</Typography>}
        <ToolButton component={Link} to="/tools/dopamenu" onClick={onClose} sx={{ mt: 1, fontSize: '0.6rem' }}>Open dopamenu</ToolButton>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1, gap: 1, flexWrap: 'wrap', rowGap: 2 }}>
        <ToolButton onClick={onClose}>Cancel</ToolButton>
        <ToolButton variant="contained" disabled={!selected.length} onClick={() => { onImport(selected.map((item) => item.title)); onClose(); }}>Import {selected.length ? `(${selected.length})` : 'selected'}</ToolButton>
      </DialogActions>
    </Dialog>
  );
}
